import React, { useState } from "react";
import EventModel from "./EventModal";

// Sample event data
interface Event {
  event_name: string;
  listing_id: string;
  uuid: string;
  start_date_time: string;
  end_date_time: string;
  short_code: string;
  bank_name: string;
  city_name: string;
  reserve_price: string;
  property_type: string;
}

interface EventCalendarInterface {
  allEventsForAdminCalendar: Event[];
  onMonthChange?: (newMonth: Date) => void;
  loading?: boolean;
}

const EventCalendar: React.FC<EventCalendarInterface> = ({
  allEventsForAdminCalendar,
  onMonthChange,
  loading = false,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDateEvents, setSelectedDateEvents] = useState<Event[]>([]);
  const [model, setModel] = useState<boolean>();
  const [selectedDateMonth, setSelectedDateMonth] = useState<{
    selectedDate: number;
    selectedMonth: number;
    selectedYear: number;
  }>({
    selectedDate: 0,
    selectedMonth: -1,
    selectedYear: -1,
  });

  const daysInMonth = (date: Date): number => {
    const year: number = date.getFullYear();
    const month: number = date.getMonth() + 1;
    return new Date(year, month, 0).getDate();
  };

  const renderCalendar = (): JSX.Element[] => {
    const totalDays: number = daysInMonth(currentDate);
    const firstDay: number = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    ).getDay();

    const calendarDays: JSX.Element[] = [];

    const today = new Date(); // Get the current date

    for (let i = 1; i <= totalDays + firstDay; i++) {
      const day: number = i - firstDay;
      const date: Date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );

      const isCurrentDate =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

      // Group events by short_code
      const eventsByShortCode: Record<string, Event[]> = {};
      allEventsForAdminCalendar.forEach((event) => {
        const eventDate: Date = new Date(event.start_date_time);
        if (
          eventDate.getDate() === day &&
          eventDate.getMonth() === currentDate.getMonth() &&
          eventDate.getFullYear() === currentDate.getFullYear()
        ) {
          if (!eventsByShortCode[event.short_code]) {
            eventsByShortCode[event.short_code] = [];
          }
          eventsByShortCode[event.short_code].push(event);
        }
      });

      calendarDays.push(
        <div
          key={i}
          className={`calendar-day col-span-1 border h-[90px] w-24 p-1 flex flex-row overflow-hidden w-full flex-col ${
            isCurrentDate ? "bg-yellow-200" : ""
          }`}
          onClick={() => handleDateClick(date)}
        >
          <div className="day flex h-6">{day > 0 ? day : ""}</div>
          <div className="flex flex-col h-[90px] w-40 rounded-lg">
            {Object.entries(eventsByShortCode).map(
              ([shortCode, events], index) => (
                <div
                  key={index}
                  className="flex flex-col p-1 bg-cyan-600 rounded full text-white flex-wrap my-1 text-xs whitespace-nowrap mx-2"
                >
                  <span className="flex text-xs">
                    {shortCode} : {events.length}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      );
    }

    return calendarDays;
  };

  const handleDateClick = (date: Date): void => {
    const clickedDateEvents: Event[] = allEventsForAdminCalendar.filter(
      (event) => {
        const eventDate: Date = new Date(event.start_date_time);
        return (
          eventDate.getDate() === date.getDate() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getFullYear() === date.getFullYear()
        );
      }
    );

    if (clickedDateEvents.length > 0) {
      setSelectedDateEvents(clickedDateEvents);
      setModel(true);
      setSelectedDateMonth({
        selectedDate: date.getDate(),
        selectedMonth: date.getMonth() + 1,
        selectedYear: date.getFullYear(),
      });
    }
  };

  const prevMonth = (): void => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1
    );
    setCurrentDate(newDate);
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  const nextMonth = (): void => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1
    );
    setCurrentDate(newDate);
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  return (
    <div className="flex flex-col justify-center items-center cursor-pointer">
      <div className="calendar-header mb-2 flex justify-between items-center w-full">
        <button
          className="flex text-white px-4 py-1 rounded-lg bg-blue-500 hover:bg-blue-700 disabled:opacity-50"
          onClick={prevMonth}
          disabled={loading}
        >
          &lt;
        </button>
        <h2 className="text-xl font-semibold">
          {currentDate.toLocaleString("default", { month: "long" })}{" "}
          {currentDate.getFullYear()}
          {loading && (
            <span className="ml-2 text-sm text-gray-500">(Loading...)</span>
          )}
        </h2>
        <button
          className="flex text-white px-4 py-1 rounded-lg bg-blue-500 hover:bg-blue-700 disabled:opacity-50"
          onClick={nextMonth}
          disabled={loading}
        >
          &gt;
        </button>
      </div>
      <div className="grid grid-cols-7 border border-gray-300 w-full shadow-md">
        <div className="calendar-day col-span-1 text-center">Sun</div>
        <div className="calendar-day col-span-1 text-center">Mon</div>
        <div className="calendar-day col-span-1 text-center">Tue</div>
        <div className="calendar-day col-span-1 text-center">Wed</div>
        <div className="calendar-day col-span-1 text-center">Thu</div>
        <div className="calendar-day col-span-1 text-center">Fri</div>
        <div className="calendar-day col-span-1 text-center">Sat</div>
        {renderCalendar()}
      </div>

      {model && (
        <div className="flex justify-center items-center bg-gray-300 bg-opacity-60 h-screen absolute w-full -top-6">
          <EventModel
            eventForSelectedDate={selectedDateEvents}
            handleModel={() => setModel(false)}
            selectedDateMonth={selectedDateMonth}
          />
        </div>
      )}
    </div>
  );
};

export default EventCalendar;
